import { z } from "zod";

/**
 * Why a user was (or wasn't) granted access. Surfaced to the client so the
 * sign-in screen can explain a rejection instead of just failing silently,
 * and so we can tell "not in the group" apart from "group check unavailable".
 */
export const accessDecisionSchema = z.enum([
  "group_member", // Verified against the Google Group via Cloud Identity.
  "allowlist", // Fallback: correct domain + present in allowed_emails.
  "existing_user", // Fallback: already admitted previously and not disabled.
  "bootstrap", // Fallback: first user on an empty allowlist. Logged loudly.
  "denied_domain", // Email is outside WORKSPACE_DOMAIN.
  "denied_not_member", // Correct domain, but not in the access group.
  "denied_unverified_email", // Google reports email_verified: false.
]);
export type AccessDecision = z.infer<typeof accessDecisionSchema>;

export const GRANTING_DECISIONS = [
  "group_member",
  "allowlist",
  "existing_user",
  "bootstrap",
] as const satisfies readonly AccessDecision[];

export const userRoleSchema = z.enum(["admin", "member"]);
export type UserRole = z.infer<typeof userRoleSchema>;

export const userSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string().nullable(),
  pictureUrl: z.string().url().nullable(),
  role: userRoleSchema,
  createdAt: z.string().datetime(),
});
export type User = z.infer<typeof userSchema>;

/** GET /api/auth/me */
export const meResponseSchema = z.discriminatedUnion("authenticated", [
  z.object({ authenticated: z.literal(false) }),
  z.object({
    authenticated: z.literal(true),
    user: userSchema,
    /** How access was most recently granted — useful for an "unverified" banner. */
    accessVia: accessDecisionSchema,
  }),
]);
export type MeResponse = z.infer<typeof meResponseSchema>;

export const apiErrorSchema = z.object({
  error: z.string(),
  message: z.string(),
  /** Present on 403s so the UI can explain exactly why access was refused. */
  decision: accessDecisionSchema.optional(),
});
export type ApiError = z.infer<typeof apiErrorSchema>;

/** GET /api/health — used by CI smoke tests and uptime checks. */
export const healthResponseSchema = z.object({
  ok: z.boolean(),
  service: z.literal("pinpoint"),
  version: z.string(),
  database: z.enum(["up", "down"]),
  /**
   * What the Google Group gate is *actually* doing, not merely whether it has
   * been configured. "configured" used to mean "the three service-account vars
   * are present", which stayed green through five days of the Cloud Identity
   * API returning 403 — a down security gate reported as healthy.
   *
   *   ok        — configured, and the most recent check succeeded
   *   failing   — configured, but the most recent check errored. Access is
   *               running on the fallback allowlist and group removals are
   *               not being enforced.
   *   unknown   — configured, but nothing has exercised it yet
   *   fallback  — not configured; the allowlist is authoritative by design
   */
  groupCheck: z.enum(["ok", "failing", "unknown", "fallback"]),
});
export type HealthResponse = z.infer<typeof healthResponseSchema>;
